import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { FormProvider } from "react-hook-form";
// components
import { Stack, Box } from "@mui/material";
import {
  ChooseRecipients,
  DraftMessage,
  Footer,
  NameCampaign,
  ReviewMessage,
  Header,
  SaveDialog,
  DeleteDialog,
} from "../../../../components/Messages";
// redux / data
import { useGetMessageData } from "../../../../hooks/useGetMessageData";
import { MessageType } from "../../../../store/messageLists";
import { CREATION_STEP_MAPPING, SESSION_KEY } from "./constants";
import { Context } from "../../../../context/Messages";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  setTemplateId,
  selectMessageComposeData,
} from "../../../../store/messageCompose";

interface IProps {
  isCreate?: boolean;
}

const CreateAndEdit = ({ isCreate }: IProps) => {
  const dispatch = useAppDispatch();
  // current message id
  const { messageId: id = "" } = useParams() as { messageId: string };
  // if user arrived at create flow with a template id it will be stored in redux here
  // this template id should only be used if isCreate is true, otherwise id returned from api should be used
  const { templateId } = useAppSelector(selectMessageComposeData);
  const {
    currentStep,
    setStepState,
    composeData,
    composeData: { status },
    isMessageLoadingOrUpdating,
    isLoading,
    methods,
    disableNext,
    disableSave,
    updateOrPostMessage,
    goToStep,
    previous,
    next,
    cancel,
    blocker,
  } = useGetMessageData({
    isCreate,
    sessionKey: SESSION_KEY,
    stepMapping: CREATION_STEP_MAPPING,
  });

  useEffect(() => {
    return () => {
      dispatch(setTemplateId(""));
    };
  }, []);

  const contextData = useMemo(
    () => ({
      setStep: setStepState,
      currentStep,
    }),
    [currentStep, id]
  );

  const steps = useMemo(
    () => [
      <NameCampaign
        key={1}
        isLoading={isMessageLoadingOrUpdating}
        type={MessageType.SM}
        templateId={templateId}
      />,
      <ChooseRecipients key={2} isLoading={isMessageLoadingOrUpdating} />,
      <DraftMessage
        key={3}
        isLoading={isMessageLoadingOrUpdating}
        templateId={templateId || composeData.templateId}
      />,
      <ReviewMessage key={4} stepMapping={CREATION_STEP_MAPPING} />,
    ],
    [isMessageLoadingOrUpdating]
  );

  return (
    <Context.Provider value={contextData}>
      <FormProvider {...methods}>
        <Stack height="100%">
          <Stack
            p={10}
            pb={0}
            maxWidth={1440}
            width="100%"
            flexGrow={1}
            m="0 auto"
            position="relative"
            data-testid={isLoading ? "create-edit-loading" : "create-edit"}
          >
            <Header
              goToStep={goToStep}
              currentStep={currentStep}
              id={id}
              isCreate={isCreate}
              messageStatus={status}
            />
            <Box
              sx={{
                flexGrow: 1,
                pt: 0,
                pb: 8,
              }}
            >
              {steps[currentStep]}
            </Box>
          </Stack>
          <Footer
            isCreate={!!isCreate}
            cancel={cancel}
            save={updateOrPostMessage}
            next={next}
            isMessageLoadingOrUpdating={isMessageLoadingOrUpdating}
            previous={previous}
            disableSave={disableSave}
            disableNext={disableNext}
            currentStepName={CREATION_STEP_MAPPING[currentStep]}
            currentStep={currentStep}
          />
          <DeleteDialog />
          <SaveDialog blocker={blocker} save={updateOrPostMessage} />
        </Stack>
      </FormProvider>
    </Context.Provider>
  );
};

export default CreateAndEdit;
